import { AdService } from '@/modules/adv/ad-service'
import { BannerState } from '@/modules/adv/banner'
import { OutOfPageBanner } from '@/modules/adv/out-of-page-banner'
import { isAdTypeHasPreroll, isPrerollAdType } from '@/modules/main-frame/ads/utils'
import { AdProviderNames, type GameEvent, PlatformType } from '@/types'
import { EventForLoggersType } from '@/types/logger.types'
import { timer } from '@/utils/helpers'

import { AdProviderStatus, AdvAction, type OutOfPageAdType } from '../../adv'
import EventBus from '../../event-bus'
import { AdResponsePayload, GameAdHandlerConfig, ShortGameEvent } from './game-ad-handler.types'

export type GameAdHandlerOptions = {
    logEvent: (event: EventForLoggersType) => void
    adService: AdService
    platform: PlatformType
}

export const PREROLL_MESSAGE_ID = 'PREROLL_MESSAGE_ID'
export const SECOND_PREROLL_MESSAGE_ID = 'SECOND_PREROLL_MESSAGE_ID'
const PREROLL_WAIT_MS = 3000
const MAX_SHORT_PREROLL_DURATION_SEC = 10

export class GameAdHandler {
    private isShowingFullscreenAd = false

    private banners: Map<OutOfPageAdType, OutOfPageBanner>

    private adService: AdService

    private originalMessages: Map<string, ShortGameEvent>

    private readonly serviceReadyPromise: Promise<AdProviderStatus>

    private logEvent: (event: EventForLoggersType) => void

    private hasTwoPrerolls = false

    private lastShownTimestamps = new Map<OutOfPageAdType, number>()

    private platform: PlatformType

    eventBus: EventBus<GameAdHandlerConfig>

    constructor(options: GameAdHandlerOptions) {
        this.logEvent = options.logEvent

        this.adService = options.adService

        this.banners = new Map()
        this.eventBus = new EventBus()
        this.originalMessages = new Map()
        this.serviceReadyPromise = this.adService.serviceReadyPromise
        this.platform = options.platform
    }

    handleMessage(event: GameEvent): Promise<unknown> {
        this.originalMessages.set(event.data.id, event)

        switch (event.data.action) {
            case 'setBannerAdvSettings':
                this.eventBus.dispatch('adMessage', {
                    action: AdvAction.setBannerAdvSettings,
                    originalMessageEvent: event as GameEvent,
                    payload: {},
                    type: 'adv',
                })
                break
            case 'preloadInterstitial':
                return this.preloadAd('interstitial', event.data.id)
            case 'preloadRewarded':
                return this.preloadAd('rewarded', event.data.id)
            case 'showInterstitial':
                return this.showAd('interstitial', event.data.id)
            case 'showRewarded':
                return this.showAd('rewarded', event.data.id)
            default:
                this.eventBus.dispatch('adMessage', {
                    originalMessageEvent: event as GameEvent,
                    payload: {
                        error: `Unknown message action "${event.data.action}"`,
                    },
                    type: 'error',
                })
                this.originalMessages.delete(event.data.id)
        }

        return Promise.resolve()
    }

    async showPreroll() {
        this.originalMessages.set(PREROLL_MESSAGE_ID, {})

        try {
            const banner = await this.preloadAd('interstitial_preroll', PREROLL_MESSAGE_ID)
            this.handleShortPreroll(banner)
            await this.showAd('interstitial_preroll', PREROLL_MESSAGE_ID)
        } catch {
            this.eventBus.dispatch('adStateChanged', {
                action: 'errored',
                adType: 'interstitial_preroll',
            })
        }
    }

    private handleShortPreroll(banner: OutOfPageBanner) {
        if (
            banner.provider === AdProviderNames.GOOGLE_INSTREAM &&
            (banner.instreamDuration || 0) < MAX_SHORT_PREROLL_DURATION_SEC &&
            this.platform === 'desktop'
        ) {
            this.hasTwoPrerolls = true
            this.originalMessages.set(SECOND_PREROLL_MESSAGE_ID, {})
            let secondBannerPromise = Promise.resolve<OutOfPageBanner | null>(null)
            try {
                secondBannerPromise = this.preloadAd('interstitial_preroll_2', SECOND_PREROLL_MESSAGE_ID)
            } catch {
                this.eventBus.dispatch('adStateChanged', {
                    action: 'errored',
                    adType: 'interstitial_preroll_2',
                })
            }

            banner.addEventListener('closed', async () => {
                try {
                    const secondBanner = await secondBannerPromise
                    if (secondBanner?.provider === AdProviderNames.GOOGLE_INSTREAM) {
                        await this.showAd('interstitial_preroll_2', SECOND_PREROLL_MESSAGE_ID)
                    } else {
                        this.banners.delete('interstitial_preroll_2')
                        throw new Error('Second preroll is not instream')
                    }
                } catch {
                    this.eventBus.dispatch('adStateChanged', {
                        action: 'errored',
                        adType: 'interstitial_preroll_2',
                    })
                }
            })
        }
    }

    private validateAdDisplayConditions(type: OutOfPageAdType, messageId: string): boolean {
        const GENERAL_MIN_TIME_BETWEEN_ADS = 5_000 // 5 seconds between ads of the same type
        const MIN_TIME_BETWEEN_PREROLL_AND_INTERSTITIAL = 20_000

        const now = Date.now()

        const lastPrerollCall = this.lastShownTimestamps.get('interstitial_preroll')
        const lastCall = this.lastShownTimestamps.get(type)

        if (
            type === 'interstitial' &&
            lastPrerollCall &&
            now - lastPrerollCall < MIN_TIME_BETWEEN_PREROLL_AND_INTERSTITIAL
        ) {
            this.handleBlockedInterstitial(messageId)
            return false
        }

        if (lastCall && now - lastCall < GENERAL_MIN_TIME_BETWEEN_ADS) {
            this.triggerMessage(
                {
                    status: 'error',
                    error: 'Too many requests for this ad type',
                },
                messageId,
            )

            return false
        }

        this.lastShownTimestamps.set(type, now)

        if (this.isShowingFullscreenAd) {
            this.triggerMessage(
                {
                    status: 'error',
                    error: 'Another ad is already open',
                },
                messageId,
            )
            return false
        }

        return true
    }

    private async showAd(type: OutOfPageAdType, messageId: string) {
        if (!this.validateAdDisplayConditions(type, messageId)) {
            return
        }

        this.eventBus.dispatch('adStateChanged', {
            action: 'start',
            adType: type,
        })

        const delayForLoader = isAdTypeHasPreroll(type) ? timer(1800) : Promise.resolve()

        let banner = this.banners.get(type)
        if (!banner) {
            try {
                banner = await this.preloadAd(type, messageId)
            } catch {
                this.eventBus.dispatch('adStateChanged', {
                    action: 'errored',
                    provider: banner?.provider,
                    adType: type,
                })

                this.isShowingFullscreenAd = false
                return
            }
        }
        this.listenToRenderedBannerEvents(banner, type, messageId)

        this.isShowingFullscreenAd = true

        banner.addEventListener('empty', () => {
            // sometimes we don't know a banner is empty till show it.
            // we can't move this code to the general empty banner handler
            // because an ad may be loading while showing other ad
            this.isShowingFullscreenAd = false
            this.triggerMessage({ status: 'close' }, messageId)

            if (isPrerollAdType(type)) {
                this.handleClosePreroll(banner, type)
            }
        })

        // the banner is in the ready status
        await delayForLoader
        banner.show()

        this.banners.delete(type)
    }

    /**
     * @return Promise<OutOfPageBanner> - a ready-to-show banner.
     * If there was an error or slot was empty - rejection will be returned
     */
    private async preloadAd(type: OutOfPageAdType, messageId: string): Promise<OutOfPageBanner> {
        try {
            await this.serviceReadyPromise
        } catch {
            // сегодня без рекламы
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject()
        }

        const message = this.originalMessages.get(messageId)

        if (!message) {
            // eslint-disable-next-line prefer-promise-reject-errors
            return Promise.reject()
        }

        let banner: OutOfPageBanner
        this.triggerMessage({ status: 'start' }, messageId)

        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (resolve, reject) => {
            try {
                const timeout = type === 'interstitial_preroll' ? PREROLL_WAIT_MS : undefined
                banner = await this.adService.requestOutOfPageAd(type, timeout)
            } catch {
                this.triggerMessage({ status: 'error', error: 'Cannot show the ad' }, messageId)
                reject(new Error('Cannot show the ad'))
                this.originalMessages.delete(messageId)
                return
            }

            const onReady = () => {
                this.triggerMessage({ status: 'done' }, messageId)
                this.banners.set(type, banner)
                resolve(banner)
            }

            const onEmpty = () => {
                this.triggerMessage({ status: 'empty' }, messageId)
                reject(new Error('Empty'))
                this.eventBus.dispatch('adStateChanged', {
                    action: 'empty',
                    provider: banner.provider,
                    adType: type,
                })
            }

            banner.addEventListener('empty', onEmpty)

            if (banner.state === BannerState.ready) {
                onReady()
            } else if (banner.state === BannerState.empty) {
                onEmpty()
            } else {
                banner.addEventListener('ready', onReady)
            }
        })
    }

    private listenToRenderedBannerEvents(banner: OutOfPageBanner, type: OutOfPageAdType, messageId: string) {
        const message = this.originalMessages.get(messageId)
        if (!message) {
            return
        }

        if (type === 'rewarded') {
            banner.addEventListener('rewarded', () => {
                this.triggerMessage({ status: 'rewarded' }, messageId)
            })
            this.eventBus.dispatch('adStateChanged', {
                action: 'rewarded',
                provider: banner.provider,
                adType: 'rewarded',
            })
        }

        banner.addEventListener('timer', (event) => {
            this.eventBus.dispatch('adStateChanged', {
                action: 'timer',
                secondsLeft: event.detail?.secondsLeft,
                provider: banner?.provider,
                adType: type,
            })
        })

        banner.addEventListener('closed', () => {
            const now = Date.now()
            this.lastShownTimestamps.set(type, now)

            this.triggerMessage({ status: 'close' }, messageId)
            this.isShowingFullscreenAd = false
            this.originalMessages.delete(messageId)

            if (isPrerollAdType(type)) {
                this.handleClosePreroll(banner, type)
            }

            this.eventBus.dispatch('adStateChanged', {
                action: 'closed',
                provider: banner.provider,
                adType: type,
            })
        })

        banner.addEventListener('rendered', () => {
            this.triggerMessage({ status: 'open' }, messageId)
            this.eventBus.dispatch('adStateChanged', {
                action: 'opened',
                provider: banner.provider,
                adType: type,
            })
        })

        banner.addEventListener('viewable', () => {
            this.triggerMessage({ status: 'show' }, messageId)

            this.eventBus.dispatch('adStateChanged', {
                action: 'impression',
                provider: banner.provider,
                adType: type,
                totalAds: this.hasTwoPrerolls ? 2 : 1,
                currentAd: type === 'interstitial_preroll_2' ? 2 : 1,
            })
        })
    }

    private triggerMessage(payload: AdResponsePayload, messageId: string) {
        const message = this.originalMessages.get(messageId)

        if (message?.data) {
            this.eventBus.dispatch('adMessage', {
                action: message.data.action,
                type: 'adv',
                originalMessageEvent: message,
                payload,
            })
        }
    }

    private handleBlockedInterstitial(id: string) {
        this.triggerMessage({ status: 'start' }, id)
        setTimeout(() => {
            this.triggerMessage({ status: 'done' }, id)
        }, 300)
        setTimeout(() => {
            this.triggerMessage({ status: 'open' }, id)
        }, 600)
        setTimeout(() => {
            this.triggerMessage({ status: 'show' }, id)
        }, 1_000)
        setTimeout(() => {
            this.triggerMessage({ status: 'close' }, id)
            this.originalMessages.delete(id)
        }, 3_000)
    }

    private handleClosePreroll(banner: OutOfPageBanner, type: OutOfPageAdType) {
        if (type === 'interstitial_preroll_2') {
            this.hasTwoPrerolls = false
        }

        this.logEvent({
            event: 'custom_event',
            eventName: 'preroll_end',
            adUnit: banner.adUnit,
            instreamDuration: banner.instreamDuration,
            watchedDuration: banner.watchedDuration,
        })
    }
}
